import { useEffect, useMemo, useRef, useState } from "react";
import {
  type MapsLifecycleEvent,
  clearMapsLifecycle,
  getMapsInstanceStatuses,
  getMapsLifecycleCounters,
  subscribeMapsLifecycle,
} from "@/lib/maps-lifecycle-bus";

type Counters = ReturnType<typeof getMapsLifecycleCounters>;

function triggerDownload(filename: string, mime: string, contents: string) {
  const blob = new Blob([contents], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function csvEscape(v: unknown): string {
  if (v === null || v === undefined) return "";
  const s = String(v);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function exportLog(
  format: "json" | "csv",
  events: ReadonlyArray<MapsLifecycleEvent>,
  counters: Counters,
) {
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  if (format === "json") {
    const payload = {
      exportedAt: new Date().toISOString(),
      userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
      url: typeof window !== "undefined" ? window.location.href : "",
      counters,
      instances: getMapsInstanceStatuses(),
      events,
    };
    triggerDownload(
      `maps-lifecycle-${stamp}.json`,
      "application/json",
      JSON.stringify(payload, null, 2),
    );
    return;
  }
  const header = ["id", "ts", "iso", "source", "action", "detail"];
  const rows = events.map((e) =>
    [
      e.id,
      e.ts,
      new Date(e.ts).toISOString(),
      e.source,
      e.action,
      e.detail ?? "",
    ].map(csvEscape).join(","),
  );
  const counterRows = [
    "",
    "# counters",
    `autocompleteInstances,${counters.autocompleteInstances}`,
    `autocompleteListeners,${counters.autocompleteListeners}`,
    `mapInstances,${counters.mapInstances}`,
    `mapListeners,${counters.mapListeners}`,
  ];
  const csv = [header.join(","), ...rows, ...counterRows].join("\n");
  triggerDownload(`maps-lifecycle-${stamp}.csv`, "text/csv", csv);
}

/**
 * Dev-only floating panel that shows live counts of Google Maps /
 * Autocomplete instances + listeners and a rolling log of lifecycle
 * actions (creation, listener add/remove, deferred cleanup, etc.).
 *
 * Mounted once at the app root. The component itself short-circuits to
 * `null` outside of `import.meta.env.DEV` so it tree-shakes out of
 * production bundles, but the lifecycle bus continues to be a no-op
 * sink there.
 *
 * Toggle with `Ctrl+Shift+M` or by clicking the floating chip.
 */
export default function MapsLifecyclePanel() {
  if (!import.meta.env.DEV) return null;
  return <MapsLifecyclePanelInner />;
}

const SOURCE_LABEL: Record<string, string> = {
  loader: "loader",
  autocomplete: "autocomplete",
  map: "map",
  wizard: "wizard",
};

const ACTION_TONE: Record<string, string> = {
  "instance:create": "text-emerald-600 dark:text-emerald-400",
  "instance:reuse": "text-sky-600 dark:text-sky-400",
  "instance:dispose": "text-orange-600 dark:text-orange-400",
  "listener:add": "text-emerald-600 dark:text-emerald-400",
  "listener:remove": "text-orange-600 dark:text-orange-400",
  "listener:duplicate": "text-destructive font-semibold",
  "cleanup:scheduled": "text-muted-foreground",
  "cleanup:cancelled": "text-sky-600 dark:text-sky-400",
  "cleanup:run": "text-orange-600 dark:text-orange-400",
  "step:mount": "text-primary font-semibold",
  "step:enter": "text-primary font-semibold",
  "step:leave": "text-muted-foreground",
  error: "text-destructive",
};

function fmtTs(ts: number) {
  const d = new Date(ts);
  return d.toLocaleTimeString(undefined, { hour12: false }) +
    "." + String(d.getMilliseconds()).padStart(3, "0");
}

const ALL_SOURCES = ["loader", "autocomplete", "map", "wizard"] as const;
type SourceKey = (typeof ALL_SOURCES)[number];

const STORAGE_KEY = "maps-lifecycle-panel:v1";
const HASH_KEY = "maps-debug";

interface PersistedState {
  open: boolean;
  sources: SourceKey[];
  hideRoutine: boolean;
}

const DEFAULT_STATE: PersistedState = {
  open: false,
  sources: [...ALL_SOURCES],
  hideRoutine: false,
};

function parseSources(raw: string | null | undefined): SourceKey[] | null {
  if (!raw) return null;
  const parts = raw
    .split(",")
    .map((s) => s.trim().toLowerCase())
    .filter((s): s is SourceKey => (ALL_SOURCES as readonly string[]).includes(s));
  return parts.length ? Array.from(new Set(parts)) : null;
}

function readPersistedState(): PersistedState {
  if (typeof window === "undefined") return DEFAULT_STATE;
  // Hash takes precedence so a shareable URL wins over a stale local pref.
  // Format: #maps-debug=open&src=map,autocomplete&hide-routine=1
  const hash = window.location.hash.replace(/^#/, "");
  const params = new URLSearchParams(hash);
  const flag = params.get(HASH_KEY);
  const srcParam = parseSources(params.get("src"));
  const hideRoutineParam = params.get("hide-routine");
  const fromHash =
    flag !== null || srcParam !== null || hideRoutineParam !== null;

  let stored: Partial<PersistedState> = {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) stored = JSON.parse(raw) as Partial<PersistedState>;
  } catch {
    /* ignore corrupted JSON */
  }

  const sources =
    srcParam ??
    (Array.isArray(stored.sources) ? parseSources(stored.sources.join(",")) : null) ??
    DEFAULT_STATE.sources;

  const open = fromHash
    ? flag === "open"
    : typeof stored.open === "boolean"
      ? stored.open
      : DEFAULT_STATE.open;

  const hideRoutine = fromHash
    ? hideRoutineParam === "1"
    : typeof stored.hideRoutine === "boolean"
      ? stored.hideRoutine
      : DEFAULT_STATE.hideRoutine;

  return { open, sources, hideRoutine };
}

function writePersistedState(state: PersistedState) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* storage quota / private mode */
  }
  // Mirror to URL hash so refreshes + copy-paste links restore the view.
  // We only touch our own keys; other hash params are preserved.
  const hash = window.location.hash.replace(/^#/, "");
  const params = new URLSearchParams(hash);
  if (state.open) params.set(HASH_KEY, "open");
  else params.delete(HASH_KEY);
  if (state.sources.length && state.sources.length < ALL_SOURCES.length) {
    params.set("src", state.sources.join(","));
  } else {
    params.delete("src");
  }
  if (state.hideRoutine) params.set("hide-routine", "1");
  else params.delete("hide-routine");
  const next = params.toString();
  const url = `${window.location.pathname}${window.location.search}${next ? `#${next}` : ""}`;
  window.history.replaceState(window.history.state, "", url);
}

const ROUTINE_ACTIONS = new Set([
  "cleanup:scheduled",
  "cleanup:cancelled",
  "instance:reuse",
]);

function MapsLifecyclePanelInner() {
  const initial = useMemo(() => readPersistedState(), []);
  const [open, setOpen] = useState(initial.open);
  const [sources, setSources] = useState<SourceKey[]>(initial.sources);
  const [hideRoutine, setHideRoutine] = useState(initial.hideRoutine);
  const [events, setEvents] = useState<ReadonlyArray<MapsLifecycleEvent>>([]);
  const [_, forceTick] = useState(0);
  const logRef = useRef<HTMLDivElement>(null);

  // Persist whenever any tracked piece of UI state changes.
  useEffect(() => {
    writePersistedState({ open, sources, hideRoutine });
  }, [open, sources, hideRoutine]);

  // React to hash edits in another tab / manual URL changes.
  useEffect(() => {
    const onHash = () => {
      const next = readPersistedState();
      setOpen(next.open);
      setSources(next.sources);
      setHideRoutine(next.hideRoutine);
    };
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    const unsub = subscribeMapsLifecycle((next) => {
      setEvents(next);
      // Counters are snapshotted on read, so just trigger a re-render.
      forceTick((t) => t + 1);
    });
    return unsub;
  }, []);

  // Keyboard shortcut.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && (e.key === "M" || e.key === "m")) {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Auto-scroll log to bottom on new events.
  useEffect(() => {
    if (!open) return;
    const el = logRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [events, open]);

  const counters = useMemo(() => getMapsLifecycleCounters(), [events]);
  const visibleEvents = useMemo(() => {
    const allow = new Set(sources);
    return events.filter((e) => {
      if (!allow.has(e.source as SourceKey)) return false;
      if (hideRoutine && ROUTINE_ACTIONS.has(e.action)) return false;
      return true;
    });
  }, [events, sources, hideRoutine]);

  const toggleSource = (s: SourceKey) =>
    setSources((curr) =>
      curr.includes(s) ? curr.filter((x) => x !== s) : [...curr, s],
    );

  return (
    <>
      {/* Floating toggle chip */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-3 left-3 z-[2147483646] rounded-full border bg-background/90 backdrop-blur px-2.5 py-1 text-[10px] font-mono shadow-sm hover:bg-accent"
        aria-label="Toggle Maps lifecycle panel"
        title="Maps lifecycle (Ctrl+Shift+M)"
      >
        🗺️ ac:{counters.autocompleteInstances}/{counters.autocompleteListeners} · map:{counters.mapInstances}/{counters.mapListeners}
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Maps lifecycle debug panel"
          className="fixed bottom-12 left-3 z-[2147483647] w-[420px] max-w-[calc(100vw-1.5rem)] rounded-lg border bg-background/95 backdrop-blur shadow-xl"
        >
          <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold">Maps lifecycle</span>
              <span className="text-[10px] text-muted-foreground">DEV</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => exportLog("json", events, counters)}
                className="rounded px-2 py-0.5 text-[10px] hover:bg-accent"
                title="Download events + counters as JSON"
              >
                Export JSON
              </button>
              <button
                type="button"
                onClick={() => exportLog("csv", events, counters)}
                className="rounded px-2 py-0.5 text-[10px] hover:bg-accent"
                title="Download events as CSV"
              >
                Export CSV
              </button>
              <button
                type="button"
                onClick={() => clearMapsLifecycle()}
                className="rounded px-2 py-0.5 text-[10px] hover:bg-accent"
              >
                Clear
              </button>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="rounded px-2 py-0.5 text-[10px] hover:bg-accent"
                aria-label="Close panel"
              >
                ✕
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 px-3 py-2 text-[11px]">
            <CounterCard label="Autocomplete instances" value={counters.autocompleteInstances} />
            <CounterCard label="Autocomplete listeners" value={counters.autocompleteListeners} />
            <CounterCard label="Map instances" value={counters.mapInstances} />
            <CounterCard label="Map listeners" value={counters.mapListeners} />
          </div>

          <div className="flex flex-wrap items-center gap-1 border-t px-3 py-1.5 text-[10px]">
            <span className="text-muted-foreground mr-1">Filter:</span>
            {ALL_SOURCES.map((s) => {
              const active = sources.includes(s);
              return (
                <button
                  key={s}
                  type="button"
                  onClick={() => toggleSource(s)}
                  aria-pressed={active}
                  className={`rounded-full border px-2 py-0.5 ${
                    active
                      ? "bg-primary/15 text-primary border-primary/30"
                      : "bg-muted/40 text-muted-foreground"
                  }`}
                >
                  {s}
                </button>
              );
            })}
            <label className="ml-auto inline-flex items-center gap-1 text-muted-foreground">
              <input
                type="checkbox"
                checked={hideRoutine}
                onChange={(e) => setHideRoutine(e.target.checked)}
                className="h-3 w-3"
              />
              Hide routine
            </label>
          </div>

          <InstanceStatusList events={events} />

          <div
            ref={logRef}
            className="max-h-72 overflow-auto border-t px-2 py-1.5 font-mono text-[10.5px] leading-tight"
          >
            {visibleEvents.length === 0 ? (
              <div className="px-1 py-2 text-muted-foreground">
                {events.length === 0
                  ? "No lifecycle events yet."
                  : "No events match the current filter."}
              </div>
            ) : (
              visibleEvents.map((e) => (
                <div key={e.id} className="flex gap-2 px-1 py-0.5">
                  <span className="shrink-0 text-muted-foreground">{fmtTs(e.ts)}</span>
                  <span className="shrink-0 w-[88px] text-muted-foreground">
                    [{SOURCE_LABEL[e.source] ?? e.source}]
                  </span>
                  <span className={`shrink-0 w-[140px] ${ACTION_TONE[e.action] ?? ""}`}>
                    {e.action}
                  </span>
                  {e.detail && (
                    <span className="truncate text-foreground/80">{e.detail}</span>
                  )}
                </div>
              ))
            )}
          </div>

          <div className="border-t px-3 py-1.5 text-[10px] text-muted-foreground">
            Ctrl+Shift+M to toggle · {visibleEvents.length}/{events.length} events
          </div>
        </div>
      )}
    </>
  );
}

function CounterCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded border bg-muted/30 px-2 py-1.5">
      <div className="text-muted-foreground text-[10px] uppercase tracking-wide">{label}</div>
      <div className="font-mono text-sm tabular-nums">{value}</div>
    </div>
  );
}

/**
 * Live per-instance status block. Reads `getMapsInstanceStatuses()` on
 * every event tick (the bus notifies subscribers on both event pushes
 * and status changes), so the listener-token / initialized flags update
 * in real time as components mount, register, and clean up.
 */
function InstanceStatusList({ events }: { events: ReadonlyArray<MapsLifecycleEvent> }) {
  // `events` is only used as a dependency to force re-reads in sync with
  // bus notifications.
  const statuses = useMemo(() => getMapsInstanceStatuses(), [events]);
  if (statuses.length === 0) {
    return (
      <div className="border-t px-3 py-1.5 text-[10px] text-muted-foreground">
        No live instances registered.
      </div>
    );
  }
  return (
    <div className="border-t px-2 py-1.5 text-[10.5px] font-mono leading-tight">
      <div className="px-1 pb-1 text-[9.5px] uppercase tracking-wide text-muted-foreground">
        Live instances
      </div>
      {statuses.map((s) => (
        <div key={s.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 px-1 py-0.5">
          <span className="shrink-0 w-[88px] text-muted-foreground">[{s.source}]</span>
          <span className="shrink-0 w-[110px] truncate" title={s.id}>{s.id}</span>
          <Pill ok={s.initialized} label={s.initialized ? "init" : "uninit"} />
          {s.source === "autocomplete" && (
            <Pill
              ok={!!s.hasPlaceListener}
              label={
                s.hasPlaceListener
                  ? `listener #${s.placeListenerToken ?? "?"}`
                  : "no listener"
              }
            />
          )}
          {s.source === "map" && (
            <Pill ok={!!s.hasMapInstance} label={s.hasMapInstance ? "map ready" : "no map"} />
          )}
          {s.pendingTeardown && (
            <span className="rounded bg-orange-500/15 text-orange-600 dark:text-orange-400 px-1.5 py-0.5">
              teardown pending
            </span>
          )}
        </div>
      ))}
    </div>
  );
}

function Pill({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className={`rounded px-1.5 py-0.5 ${
        ok
          ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400"
          : "bg-muted text-muted-foreground"
      }`}
    >
      {label}
    </span>
  );
}

